import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { CustomFontViewMode, displayContent } from 'modules/templateEditor/common';
import { ModalType, useModalContext } from 'modules/templateEditor/context';
import { useContentEditorPreferencesContext } from 'modules/templateEditor/context/ContentEditorPreferencesContext';
import { PrimaryButton, SecondaryButton, SecondaryDestructiveButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { Add16, Delete, Edit16 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { ModalBody } from 'ui/modal';
import { H4 } from 'ui/theme';
import { isValidUrl } from 'utils/validation';

import { CustomFontsEmptyState } from './CustomFontsEmptyState';

const StyledModalBody = styled(ModalBody)`
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundSecondary};
  border: none;
  display: flex;
  gap: ${({ theme }) => theme.spacing.values.plusHalf};
  height: ${({ theme }) => theme.spacing.utils.pxToRem(453)};
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.values.plusHalf};
`;

const DisplayPanel = styled.div`
  align-items: stretch;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

const DisplayPanelHeader = styled(H4)`
  margin-bottom: ${({ theme }) => theme.spacing.values.base};
`;

const DisplayPanelButtonBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.values.threeEights};
  margin-top: ${({ theme }) => theme.spacing.values.base};
`;

const Divider = styled.div`
  background-color: #bdbdbd;
  flex: 0 0 auto;
  width: ${({ theme }) => theme.spacing.values.single};
`;

const ListPanel = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  width: ${({ theme }) => theme.spacing.utils.pxToRem(200)};
`;

const FontList = styled.div`
  flex: 1 1 auto;
  margin-top: ${({ theme }) => theme.spacing.values.base};
  overflow: hidden auto;

  > ul {
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    padding: 0;

    > li {
      cursor: pointer;
      margin-bottom: ${({ theme }) => theme.spacing.values.half};

      & :hover {
        color: ${({ theme }) => theme.colors.primaryColors.cyan100};
      }
    }
  }
`;

const SelectedFont = styled.li`
  color: ${({ theme }) => theme.colors.primaryColors.cyan100};
  cursor: default !important;
  font-weight: 600;
`;

const AddFontButton = styled(SecondaryButton)`
  flex: 0 0 auto;
  width: 100%;
`;

const FontDetails = styled.dl`
  font-size: 14px;
  > dt {
    margin-top: ${({ theme }) => theme.spacing.values.plusHalf};
  }

  > dd {
    margin-top: ${({ theme }) => theme.spacing.values.half};
  }
`;

export const TabCustomFonts = () => {
  const [mode, setMode] = useState<CustomFontViewMode>(CustomFontViewMode.Empty);
  const [fontName, setFontName] = useState<string>('');
  const [fontFamily, setFontFamily] = useState<string>('');
  const [fontUrl, setFontUrl] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [, cssUrlIsInvalidErrorMessage] = isValidUrl(fontUrl.trim());
  const { customFonts, addCustomFont, deleteCustomFont, editCustomFont } =
    useContentEditorPreferencesContext();

  const { openModal, closeModal } = useModalContext();

  const fontNameIsUnique =
    mode !== CustomFontViewMode.Create ||
    customFonts.findIndex((font) => font.name === fontName) === -1;

  const saveIsDisabled =
    fontName.trim().length === 0 ||
    fontFamily.trim().length === 0 ||
    fontUrl.trim().length === 0 ||
    !!cssUrlIsInvalidErrorMessage ||
    !fontNameIsUnique;

  const onSaveCustomFont = () => {
    if (mode === CustomFontViewMode.Create) {
      addCustomFont({
        name: fontName.trim(),
        fontFamily: fontFamily.trim(),
        url: fontUrl.trim(),
      });
    } else {
      editCustomFont(customFonts[selectedIndex].name, {
        name: fontName.trim(),
        fontFamily: fontFamily.trim(),
        url: fontUrl.trim(),
      });
    }
  };

  const onDeleteCustomFont = () => {
    deleteCustomFont(fontName);
  };

  useEffect(() => {
    if (mode === CustomFontViewMode.View || !customFonts.length) {
      // customFonts changed from the View mode => a font was deleted (select the first font)
      setSelectedIndex(0);
    } else {
      // customFonts changed from the Create or Edit mode => select the new/edited font
      setSelectedIndex(
        Math.max(
          customFonts.findIndex((font) => font.name === fontName),
          0,
        ),
      );
    }
    setMode(customFonts.length ? CustomFontViewMode.View : CustomFontViewMode.Empty);
    closeModal(ModalType.ConfirmCustomFontModal);
  }, [customFonts]);

  useEffect(() => {
    if (mode === CustomFontViewMode.Create) {
      setFontName('');
      setFontFamily('');
      setFontUrl('');
    } else {
      setFontName(customFonts[selectedIndex]?.name || '');
      setFontFamily(customFonts[selectedIndex]?.fontFamily || '');
      setFontUrl(customFonts[selectedIndex]?.url || '');
    }
  }, [customFonts, mode]);

  useEffect(() => {
    setMode(customFonts.length ? CustomFontViewMode.View : CustomFontViewMode.Empty);
    setFontName(customFonts[selectedIndex]?.name || '');
    setFontFamily(customFonts[selectedIndex]?.fontFamily || '');
    setFontUrl(customFonts[selectedIndex]?.url || '');
  }, [selectedIndex]);

  const openConfirmationModal = () =>
    openModal(ModalType.ConfirmCustomFontModal, {
      mode,
      saveIsDisabled,
      onSaveCustomFont,
      onDeleteCustomFont,
    });

  return (
    <StyledModalBody>
      {mode === CustomFontViewMode.Empty ? (
        <CustomFontsEmptyState setMode={setMode} />
      ) : (
        <>
          <ListPanel>
            <AddFontButton
              data-test="btn-add-custom-font"
              isDisabled={mode === CustomFontViewMode.Create}
              onClick={() => setMode(CustomFontViewMode.Create)}
            >
              <Add16 width={16} height={16} />
              <span>{displayContent.btnAddCustomFont}</span>
            </AddFontButton>
            <FontList>
              <ul>
                {mode === CustomFontViewMode.Create && (
                  <SelectedFont key={`font-new`}>
                    {fontName || displayContent.labelNewFontName}
                  </SelectedFont>
                )}
                {customFonts.map((font, idx) => {
                  return idx === selectedIndex && mode !== CustomFontViewMode.Create ? (
                    <SelectedFont key={`font-${idx}`}>{font.name}</SelectedFont>
                  ) : (
                    <li key={`font-${idx}`} onClick={() => setSelectedIndex(idx)}>
                      {font.name}
                    </li>
                  );
                })}
              </ul>
            </FontList>
          </ListPanel>
          <Divider />
          <DisplayPanel>
            {mode === CustomFontViewMode.View ? (
              <>
                <DisplayPanelHeader>{fontName}</DisplayPanelHeader>
                <FontDetails>
                  <dt>{displayContent.labelFontName}</dt>
                  <dd>{fontName}</dd>
                  <dt>{displayContent.labelFontFamily}</dt>
                  <dd>{fontFamily}</dd>
                  <dt>{displayContent.labelFontURL}</dt>
                  <dd>{fontUrl}</dd>
                </FontDetails>
                <DisplayPanelButtonBar>
                  <SecondaryButton onClick={() => setMode(CustomFontViewMode.Edit)}>
                    <Edit16 width={16} height={16} />
                    <span>{displayContent.btnEditFont}</span>
                  </SecondaryButton>
                  <SecondaryDestructiveButton onClick={openConfirmationModal}>
                    <Delete width={16} height={16} />
                    <span>{displayContent.btnDeleteFont}</span>
                  </SecondaryDestructiveButton>
                </DisplayPanelButtonBar>
              </>
            ) : (
              <>
                <DisplayPanelHeader>
                  {mode === CustomFontViewMode.Create
                    ? displayContent.headerNewFont
                    : displayContent.headerEditFont}
                </DisplayPanelHeader>
                <FormField
                  errorText={!fontNameIsUnique ? displayContent.errFontNameUnique : ''}
                  hasError={!fontNameIsUnique}
                  inputId="fontName"
                  labelText={displayContent.labelFontName}
                >
                  <InputText
                    id="fontName"
                    isReadOnly={mode === CustomFontViewMode.Edit}
                    onChange={setFontName}
                    placeholder={displayContent.labelFontName}
                    value={fontName}
                  />
                </FormField>
                <FormField
                  hasError={false}
                  inputId="fontFamily"
                  labelText={displayContent.labelFontFamily}
                >
                  <InputText
                    id="fontFamily"
                    onChange={setFontFamily}
                    placeholder={displayContent.labelFontFamily}
                    value={fontFamily}
                  />
                </FormField>
                <FormField
                  errorText={cssUrlIsInvalidErrorMessage}
                  hasError={!!(cssUrlIsInvalidErrorMessage && fontUrl.length > 0)}
                  helpText={
                    !(!!cssUrlIsInvalidErrorMessage && fontUrl.length > 0) &&
                    displayContent.strFontUrlHelpText
                  }
                  inputId="fontUrl"
                  labelText={displayContent.labelFontURL}
                >
                  <InputText
                    id="fontUrl"
                    onChange={setFontUrl}
                    placeholder={displayContent.labelFontURL}
                    value={fontUrl}
                  />
                </FormField>
                <DisplayPanelButtonBar>
                  <PrimaryButton
                    isDisabled={saveIsDisabled}
                    onClick={() =>
                      mode === CustomFontViewMode.Create
                        ? onSaveCustomFont()
                        : openConfirmationModal()
                    }
                  >
                    {displayContent.btnSaveFont}
                  </PrimaryButton>
                </DisplayPanelButtonBar>
              </>
            )}
          </DisplayPanel>
        </>
      )}
    </StyledModalBody>
  );
};
